import { useState, useEffect, useCallback } from "react";
import axios from "axios";
import { useNavigate, useSearchParams } from "react-router-dom";
import {
  Users, Plus, Search, LogIn, Crown, X, Loader2, Lock, Globe, ArrowRight, UserPlus,
} from "lucide-react";
import { toast } from "sonner";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function SquadsPage() {
  const navigate = useNavigate();
  const [params] = useSearchParams();
  const [squads, setSquads] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showCreate, setShowCreate] = useState(false);
  const [form, setForm] = useState({ name: "", description: "", privacy: "open" });
  const [creating, setCreating] = useState(false);
  const [code, setCode] = useState("");
  const [joining, setJoining] = useState(false);
  const [q, setQ] = useState("");
  const [results, setResults] = useState(null);
  const [searching, setSearching] = useState(false);

  useEffect(() => {
    const c = params.get("code");
    if (c) setCode(c);
  }, [params]);

  const load = useCallback(async () => {
    try {
      const res = await axios.get(`${API}/squads/mine`);
      setSquads(res.data.squads || []);
    } catch { toast.error("Failed to load squads"); }
    finally { setLoading(false); }
  }, []);
  useEffect(() => { load(); }, [load]);

  const handleCreate = async () => {
    if (form.name.trim().length < 2) { toast.error("Name must be at least 2 characters"); return; }
    setCreating(true);
    try {
      const res = await axios.post(`${API}/squads`, form);
      toast.success("Squad created!");
      setShowCreate(false);
      setForm({ name: "", description: "", privacy: "open" });
      navigate(`/app/squads/${res.data.id}`);
    } catch (err) { toast.error(err.response?.data?.detail || "Failed to create squad"); }
    finally { setCreating(false); }
  };

  const handleJoinCode = async () => {
    if (!code.trim()) return;
    setJoining(true);
    try {
      const res = await axios.post(`${API}/squads/join`, { invite_code: code.trim().toLowerCase() });
      if (res.data.status === "joined") {
        toast.success("Joined squad!");
        navigate(`/app/squads/${res.data.squad.id}`);
      } else { toast.success("Request sent — waiting for approval"); }
      setCode("");
      load();
    } catch (err) { toast.error(err.response?.data?.detail || "Invalid invite code"); }
    finally { setJoining(false); }
  };

  const handleSearch = async (val) => {
    setQ(val);
    if (val.trim().length < 2) { setResults(null); return; }
    setSearching(true);
    try {
      const res = await axios.get(`${API}/squads/search?q=${encodeURIComponent(val.trim())}`);
      setResults(res.data.squads || []);
    } catch { setResults([]); }
    finally { setSearching(false); }
  };

  const joinFromDiscover = async (squad) => {
    try {
      const res = await axios.post(`${API}/squads/join`, { squad_id: squad.id });
      if (res.data.status === "joined") { toast.success("Joined!"); navigate(`/app/squads/${squad.id}`); }
      else { toast.success("Request sent — waiting for approval"); handleSearch(q); load(); }
    } catch (err) { toast.error(err.response?.data?.detail || "Couldn't join"); }
  };

  return (
    <div className="page-container fade-in" data-testid="squads-page">
      <div className="page-header">
        <div>
          <h1 style={{ display: "flex", alignItems: "center", gap: "0.5rem" }}><Users size={24} /> Squads</h1>
          <p style={{ color: "var(--text-secondary)" }}>Grind your job hunt with friends. Compare stats, climb the leaderboard, celebrate wins.</p>
        </div>
        <button className="btn-primary" data-testid="create-squad-btn" onClick={() => setShowCreate(true)}>
          <Plus size={16} /> Create Squad
        </button>
      </div>

      {/* Join + discover */}
      <div className="card" style={{ padding: "1.2rem", marginBottom: "1.4rem", display: "flex", flexWrap: "wrap", gap: "1.5rem" }}>
        <div style={{ flex: "1 1 260px" }}>
          <label style={{ fontSize: "0.8rem", color: "var(--text-secondary)", display: "flex", alignItems: "center", gap: 6, marginBottom: 6 }}><LogIn size={14} /> Join with an invite code</label>
          <div style={{ display: "flex", gap: 8 }}>
            <input className="modal-input" data-testid="join-code-input" value={code}
              onChange={(e) => setCode(e.target.value)} placeholder="e.g. s9ei40b" style={{ flex: 1 }} />
            <button className="btn-secondary" data-testid="join-code-btn" onClick={handleJoinCode} disabled={joining}>
              {joining ? <Loader2 size={15} className="spin" /> : "Join"}
            </button>
          </div>
        </div>
        <div style={{ flex: "1 1 260px" }}>
          <label style={{ fontSize: "0.8rem", color: "var(--text-secondary)", display: "flex", alignItems: "center", gap: 6, marginBottom: 6 }}><Search size={14} /> Discover squads</label>
          <input className="modal-input" data-testid="discover-search-input" value={q}
            onChange={(e) => handleSearch(e.target.value)} placeholder="Search by name…" style={{ width: "100%" }} />
        </div>
      </div>

      {/* Discover results */}
      {results !== null && (
        <div style={{ marginBottom: "1.4rem" }} data-testid="discover-results">
          {searching ? <div style={{ color: "var(--text-secondary)" }}><Loader2 size={15} className="spin" /> Searching…</div>
            : results.length === 0 ? <div style={{ color: "var(--text-muted)", fontSize: "0.88rem" }}>No squads found.</div>
            : results.map((s) => (
              <div key={s.id} className="card" data-testid={`discover-squad-${s.id}`}
                style={{ padding: "0.9rem 1.1rem", display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: 8 }}>
                <div>
                  <div style={{ fontWeight: 600, display: "flex", alignItems: "center", gap: 6 }}>
                    {s.name} {s.privacy === "request" ? <Lock size={13} /> : <Globe size={13} />}
                    <span style={{ fontSize: "0.75rem", color: "var(--text-muted)" }}>· {s.member_count} members</span>
                  </div>
                  {s.description && <div style={{ fontSize: "0.82rem", color: "var(--text-secondary)" }}>{s.description}</div>}
                </div>
                {s.is_member ? <span style={{ fontSize: "0.8rem", color: "#10b981" }}>Joined</span>
                  : s.request_pending ? <span style={{ fontSize: "0.8rem", color: "#f59e0b" }}>Requested</span>
                  : <button className="btn-secondary" data-testid={`discover-join-${s.id}`} onClick={() => joinFromDiscover(s)}>
                      <UserPlus size={14} /> {s.privacy === "request" ? "Request" : "Join"}
                    </button>}
              </div>
            ))}
        </div>
      )}

      {/* My squads */}
      <h3 style={{ marginBottom: "0.8rem" }}>Your Squads</h3>
      {loading ? <div className="analytics-loading" data-testid="squads-loading"><div className="processing-spinner" /></div>
        : squads.length === 0 ? (
          <div className="card" data-testid="squads-empty" style={{ padding: "2.5rem", textAlign: "center" }}>
            <Users size={40} style={{ opacity: 0.35 }} />
            <h3>No squads yet</h3>
            <p style={{ color: "var(--text-secondary)" }}>Create one and invite your job-hunting friends.</p>
            <button className="btn-primary" onClick={() => setShowCreate(true)}><Plus size={16} /> Create Squad</button>
          </div>
        ) : (
          <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fill, minmax(260px, 1fr))", gap: "1rem" }}>
            {squads.map((s) => (
              <div key={s.id} className="card squad-card" data-testid={`squad-card-${s.id}`}
                onClick={() => navigate(`/app/squads/${s.id}`)}
                style={{ padding: "1.3rem", cursor: "pointer", transition: "transform 0.15s, border-color 0.15s" }}
                onMouseEnter={(e) => { e.currentTarget.style.transform = "translateY(-3px)"; e.currentTarget.style.borderColor = "#6366f1"; }}
                onMouseLeave={(e) => { e.currentTarget.style.transform = "none"; e.currentTarget.style.borderColor = ""; }}>
                <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start" }}>
                  <div style={{ width: 42, height: 42, borderRadius: 12, background: "rgba(99,102,241,0.15)", color: "#6366f1", display: "flex", alignItems: "center", justifyContent: "center" }}>
                    <Users size={22} />
                  </div>
                  {s.my_role === "admin" && <span style={{ fontSize: "0.68rem", padding: "2px 8px", borderRadius: 999, background: "rgba(245,158,11,0.15)", color: "#f59e0b", display: "flex", alignItems: "center", gap: 3 }}><Crown size={11} /> Admin</span>}
                </div>
                <h3 style={{ margin: "0.8rem 0 0.2rem" }}>{s.name}</h3>
                <div style={{ fontSize: "0.8rem", color: "var(--text-muted)", display: "flex", alignItems: "center", gap: 5 }}>
                  {s.privacy === "request" ? <Lock size={12} /> : <Globe size={12} />}{s.member_count} member{s.member_count !== 1 ? "s" : ""}
                </div>
                {s.description && <p style={{ fontSize: "0.84rem", color: "var(--text-secondary)", marginTop: 8 }}>{s.description}</p>}
                <div style={{ marginTop: 12, color: "#6366f1", fontSize: "0.84rem", display: "flex", alignItems: "center", gap: 4 }}>
                  Open <ArrowRight size={14} />
                </div>
              </div>
            ))}
          </div>
        )}

      {/* Create modal */}
      {showCreate && (
        <div className="modal-overlay" data-testid="create-squad-modal" onClick={() => setShowCreate(false)}
          style={{ position: "fixed", inset: 0, background: "rgba(0,0,0,0.55)", display: "flex", alignItems: "center", justifyContent: "center", zIndex: 100, padding: "1rem" }}>
          <div className="card" onClick={(e) => e.stopPropagation()} style={{ padding: "1.8rem", width: "min(460px, 100%)" }}>
            <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: "1rem" }}>
              <h2 style={{ margin: 0 }}>Create a Squad</h2>
              <button onClick={() => setShowCreate(false)} style={{ background: "none", border: "none", cursor: "pointer", color: "var(--text-muted)" }}><X size={20} /></button>
            </div>
            <label style={{ fontSize: "0.82rem", color: "var(--text-secondary)" }}>Squad name</label>
            <input className="modal-input" data-testid="squad-name-input" value={form.name} maxLength={60}
              onChange={(e) => setForm({ ...form, name: e.target.value })} placeholder="Job Hunt Crew" style={{ width: "100%", marginBottom: 12 }} />
            <label style={{ fontSize: "0.82rem", color: "var(--text-secondary)" }}>Description (optional)</label>
            <textarea className="modal-input" data-testid="squad-desc-input" value={form.description} rows={2} maxLength={300}
              onChange={(e) => setForm({ ...form, description: e.target.value })} placeholder="What's this squad about?" style={{ width: "100%", marginBottom: 12, resize: "vertical" }} />
            <label style={{ fontSize: "0.82rem", color: "var(--text-secondary)", display: "block", marginBottom: 6 }}>Who can join?</label>
            <div style={{ display: "flex", gap: 10, marginBottom: "1.4rem" }}>
              {[{ v: "open", icon: Globe, t: "Open", d: "Anyone with the code or search can join" },
                { v: "request", icon: Lock, t: "Request", d: "You approve each join request" }].map(({ v, icon: Icon, t, d }) => (
                <button key={v} data-testid={`squad-privacy-${v}`} onClick={() => setForm({ ...form, privacy: v })}
                  style={{ flex: 1, textAlign: "left", padding: "0.8rem", borderRadius: 10, cursor: "pointer",
                    border: `1.5px solid ${form.privacy === v ? "#6366f1" : "var(--border-main)"}`,
                    background: form.privacy === v ? "rgba(99,102,241,0.08)" : "transparent" }}>
                  <div style={{ display: "flex", alignItems: "center", gap: 6, fontWeight: 600, fontSize: "0.88rem" }}><Icon size={15} /> {t}</div>
                  <div style={{ fontSize: "0.74rem", color: "var(--text-muted)", marginTop: 3 }}>{d}</div>
                </button>
              ))}
            </div>
            <button className="btn-primary" data-testid="create-squad-submit" onClick={handleCreate} disabled={creating} style={{ width: "100%" }}>
              {creating ? <Loader2 size={16} className="spin" /> : <Plus size={16} />} Create Squad
            </button>
          </div>
        </div>
      )}
    </div>
  );
}
